import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useMemo } from "react"
import styled from "styled-components"

import AppDatePicker from "../../../../../../_shared/components/FormItems/PlainDatePicker"
import StyledAppHeading from "../../../../../../_shared/components/Typography/Heading"

// ----------------------------------------------------------------------------

function _DateChangeForm(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, values, onValueChange, eventData, isNewEvent } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const { firstDay, sortedDays } = useMemo(() => {
        const sortedDays = eventData?.days
        sortedDays?.sort(sortFunction)
        const firstDay = sortedDays && sortedDays[0]?.date
        return { firstDay, sortedDays }
    }, [eventData?.days])

    const newDays = useMemo(() => {
        if (getShouldUpdateDate()) {
            return getNewDays()
        } else {
            return values?.days
        }
        // eslint-disable-next-line
    }, [values?.eventStartDate])

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        if (!values?.eventStartDate) {
            onValueChange &&
                onValueChange({
                    shouldUpdateDate: false,
                    eventStartDate: firstDay,
                })
        }
        // eslint-disable-next-line
    }, [values])

    useEffect(() => {
        const shouldUpdateDate = getShouldUpdateDate()
        onValueChange && onValueChange({ ...values, shouldUpdateDate })
        // eslint-disable-next-line
    }, [values?.eventStartDate, firstDay])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function getShouldUpdateDate() {
        return firstDay && !(firstDay === values?.eventStartDate)
    }

    function handleChange(newDate) {
        onValueChange && onValueChange({ ...values, eventStartDate: newDate })
    }

    function getNewDays() {
        const dayDiffs = sortedDays.map((d) => getDaysPassed(firstDay, d.date))
        return sortedDays?.map((day, index) => {
            return {
                ...day,
                date: dayjs(values?.eventStartDate)
                    .add(dayDiffs[index], "days")
                    .format("YYYY-MM-DD"),
            }
        })
    }

    function getDaysPassed(d1, d2) {
        const d1AsDate = dayjs(d1)
        const d2AsDate = dayjs(d2)
        return Math.abs(d1AsDate.diff(d2AsDate, "days"))
    }

    function getDayBookings(day) {
        return eventData?.slots?.filter((s) => s.date === day.date && s.booker)
            ?.length
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <StyledAppHeading className="heading" boldness="bold">
                Data di inizio evento
            </StyledAppHeading>
            <AppDatePicker
                value={values?.eventStartDate}
                onChange={handleChange}
            />

            {!isNewEvent && (
                <div className="change-report">
                    <StyledAppHeading className="heading" boldness="bold">
                        Resoconto cambiamenti
                    </StyledAppHeading>
                    <ul>
                        {getShouldUpdateDate() &&
                            newDays.map((day, index) => (
                                <li className="day" key={day.date}>
                                    La giornata di{" "}
                                    <strong className="date-from">
                                        {dayjs(sortedDays[index]?.date).format(
                                            "dddd D MMMM YYYY"
                                        )}
                                    </strong>{" "}
                                    verrà spostata a{" "}
                                    <strong className="date-to">
                                        {dayjs(day.date).format(
                                            "dddd DD MMMM YYYY"
                                        )}
                                    </strong>{" "}
                                    (
                                    <strong className="bookings-count">
                                        {getDayBookings(sortedDays[index])}
                                    </strong>{" "}
                                    prenotazioni coinvolte)
                                </li>
                            ))}
                    </ul>
                    <strong>
                        Gli utenti iscritti all&apos;evento riceveranno
                        un&apos;email di notifica della modifica.
                    </strong>
                </div>
            )}
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_DateChangeForm.propTypes = {
    className: PropTypes.string.isRequired,
    values: PropTypes.object,
    onValueChange: PropTypes.func,
    eventData: PropTypes.object,
    isNewEvent: PropTypes.bool,
}

_DateChangeForm.defaultProps = {}

// ----------------------------------------------------------------------------

const DateChangeForm = styled(_DateChangeForm)`
    & {
        .heading {
            margin-top: 30px;
        }
        .change-report {
            ul {
                li.day {
                    font-size: 16px;
                    .date-from {
                        color: ${({ theme }) => theme.colors.errorColor};
                    }
                    .date-to {
                        color: ${({ theme }) => theme.colors.success};
                    }
                    .bookings-count {
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default DateChangeForm

export function sortFunction(a, b) {
    const aAsDate = dayjs(a?.date)
    const bAsDate = dayjs(b?.date)
    return (aAsDate > bAsDate) - (aAsDate < bAsDate)
}
